import { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, CalendarDays, Clock3, Video, ArrowRight, GraduationCap, Users, Award } from "lucide-react";
import { trackMeta } from "../../analytics/metaPixel";
import { usePresaleCountdown, CountdownDigits } from "./PresaleCountdown";

const EASE = [0.22, 1, 0.36, 1];

const COURSE_POINTS = [
    { icon: GraduationCap, text: "Setki pomysłów: słownictwo, gramatyka, komunikacja" },
    { icon: Users, text: "Przedszkolaki, dzieci, młodzież i dorośli; stacjonarnie i online" },
    { icon: Award, text: "Jeden z bestsellerów edunation" },
];

export default function ThankYou() {
    const countdown = usePresaleCountdown();

    useEffect(() => {
        trackMeta("CompleteRegistration", { status: true });
    }, []);

    return (
        <div className="min-h-screen bg-canvas text-brand-ink font-sans antialiased" data-testid="thankyou-page">
            <header className="border-b border-brand-tint2 bg-white/85 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
                    <a href="/" data-testid="thankyou-logo-link" aria-label="edunation — strona główna">
                        <img src="/assets/logo.png" alt="edunation — logo" className="h-8 w-auto" />
                    </a>
                </div>
            </header>

            <main className="px-4 sm:px-8 py-16 sm:py-24">
                <div className="max-w-2xl mx-auto text-center">
                    <motion.div
                        initial={{ scale: 0, rotate: -30 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="mx-auto grid place-items-center w-20 h-20 rounded-full bg-brand-tint"
                    >
                        <CheckCircle2 className="w-11 h-11 text-brand-deep" aria-hidden="true" />
                    </motion.div>

                    <span className="block overflow-hidden mt-8 pb-1">
                        <motion.span
                            className="block text-4xl sm:text-5xl font-extrabold tracking-tight"
                            initial={{ y: "110%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                            data-testid="thankyou-heading"
                        >
                            Dziękujemy za zapis!
                        </motion.span>
                    </span>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
                        className="mt-5 text-base sm:text-lg leading-relaxed text-brand-gray"
                    >
                        Wszystkie informacje o udziale wyślemy na Twój adres e-mail.
                        Po webinarze otrzymasz także nagranie i certyfikat.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
                        className="mt-7 flex flex-wrap justify-center gap-3"
                    >
                        <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-tint2 px-4 py-2 text-sm font-semibold">
                            <CalendarDays className="w-4 h-4 text-brand" aria-hidden="true" /> 3.11.2026 (wtorek)
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-tint2 px-4 py-2 text-sm font-semibold">
                            <Clock3 className="w-4 h-4 text-brand" aria-hidden="true" /> godz. 21:00
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-tint2 px-4 py-2 text-sm font-semibold">
                            <Video className="w-4 h-4 text-brand" aria-hidden="true" /> Spotkanie online
                        </span>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.85, delay: 0.75, ease: EASE }}
                    className="max-w-4xl mx-auto mt-16"
                >
                    <div className="text-center mb-9">
                        <h2
                            className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink"
                            data-testid="course-section-heading"
                        >
                            Zapraszamy na dokładkę
                        </h2>
                        <p className="mt-3 text-base sm:text-lg leading-relaxed text-brand-gray max-w-xl mx-auto">
                            Chcesz więcej gier i zabaw na swoje zajęcia? Ten kurs
                            to nasz bestseller.
                        </p>
                    </div>
                    <div
                        className="rounded-[2.5rem] bg-gradient-to-br from-brand-darker via-brand-deep to-brand p-2 shadow-[0_50px_100px_-40px_rgba(143,0,80,0.7)]"
                        data-testid="course-card"
                    >
                        <div className="rounded-[2rem] bg-white overflow-hidden">
                            <div className="px-7 sm:px-10 pt-7 sm:pt-10">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="inline-flex rounded-full bg-brand-tint px-5 py-2.5 text-sm sm:text-base font-extrabold uppercase tracking-wide text-brand-deep" data-testid="course-offer-badge">
                                        Kurs online · oferta dodatkowa
                                    </span>
                                    <span className="inline-flex rounded-full bg-brand-ink px-4 py-2 font-mono text-[10px] sm:text-xs font-bold tracking-[0.14em] uppercase text-white">
                                        {countdown.expired ? "Kurs online" : "Promocja do 10.11"}
                                    </span>
                                </div>
                                <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight">
                                    Gry i zabawy językowe
                                </h2>
                                <p className="mt-3 text-sm sm:text-base leading-relaxed text-brand-gray max-w-3xl">
                                    Więcej pomysłów. Więcej języka. Więcej zaangażowania. Poznaj setki
                                    pomysłów na gry i zabawy, dzięki którym przećwiczysz z uczniami
                                    słownictwo, gramatykę i komunikację, na zajęciach stacjonarnych
                                    i online.
                                </p>
                            </div>
                            {!countdown.expired ? (
                                <div
                                    className="mx-7 sm:mx-10 mt-6 rounded-2xl bg-brand-tint px-5 sm:px-7 py-5 flex flex-wrap items-center gap-x-8 gap-y-5"
                                    data-testid="course-promo-panel"
                                >
                                    <div>
                                        <p className="text-sm font-semibold text-brand-gray">
                                            <span className="line-through">399 zł cena regularna</span>
                                        </p>
                                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-2">
                                            <span className="text-4xl sm:text-5xl font-extrabold text-brand-deep leading-none" data-testid="course-price">
                                                od 249 PLN
                                            </span>
                                            <span className="rounded-full bg-brand-deep px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wide text-white">
                                                duża obniżka
                                            </span>
                                        </div>
                                        <p className="mt-2.5 text-xs sm:text-sm font-semibold text-brand-ink max-w-xs" data-testid="course-price-note">
                                            Promocja na bestsellerowy kurs, tylko do północy 10.11
                                        </p>
                                    </div>
                                    <div
                                        className="flex flex-wrap items-center gap-3"
                                        data-testid="course-countdown"
                                    >
                                        <p className="text-xs sm:text-sm font-extrabold uppercase tracking-[0.14em] text-brand-deep w-full sm:w-auto">
                                            Promocja kończy się za
                                        </p>
                                        <CountdownDigits time={countdown} testIdPrefix="course-countdown" />
                                    </div>
                                </div>
                            ) : (
                                <div className="mx-7 sm:mx-10 mt-6">
                                    <div className="flex items-end gap-3">
                                        <span className="text-3xl font-extrabold text-brand-deep" data-testid="course-price">399 PLN</span>
                                    </div>
                                    <p className="mt-2 text-sm font-bold text-brand-deep" data-testid="course-price-note">
                                        Cena regularna. Oferta specjalna zakończyła się.
                                    </p>
                                </div>
                            )}
                            <div className="grid md:grid-cols-2 items-center gap-6 px-7 sm:px-10 py-7 sm:py-10">
                                <div className="relative rounded-3xl bg-gradient-to-br from-brand-tint to-white grid place-items-center p-6 sm:p-8">
                                    <img
                                        src="/assets/course-gry.webp"
                                        loading="lazy"
                                        alt="Kurs „Gry i zabawy językowe” — edunation"
                                        className="w-full max-h-72 md:max-h-96 object-contain drop-shadow-xl"
                                        data-testid="course-image"
                                    />
                                </div>
                                <div>
                                    <ul className="space-y-2.5">
                                        {COURSE_POINTS.map((p) => (
                                            <li key={p.text} className="flex items-center gap-3 text-sm sm:text-base font-semibold text-brand-ink">
                                                <span className="grid place-items-center w-8 h-8 shrink-0 rounded-lg bg-brand-tint text-brand-deep">
                                                    <p.icon className="w-4 h-4" aria-hidden="true" />
                                                </span>
                                                {p.text}
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        href="https://gameweek.edunation.com.pl/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="course-cta-button"
                                        className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-deep px-8 py-4 text-base font-extrabold text-white shadow-[0_18px_40px_-14px_rgba(196,0,111,0.7)] transition-[transform,background-color] duration-200 hover:bg-brand hover:scale-[1.015] active:scale-[0.98]"
                                    >
                                        Zobacz kurs
                                        <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="mt-8 text-center">
                        <a
                            href="/"
                            className="text-sm font-semibold text-brand-gray underline underline-offset-4 decoration-brand-tint2 hover:text-brand-deep transition-colors"
                            data-testid="back-home-link"
                        >
                            ← Wróć na stronę webinaru
                        </a>
                    </p>
                </motion.div>
            </main>
        </div>
    );
}
